import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, RefreshCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  JobsTable,
  JobsTableSkeleton,
} from "@/components/engine/JobsTable";
import { CfoIncidentPanel } from "@/components/engine/CfoIncidentPanel";
import { JobRunLiveSheet } from "@/components/engine/JobRunLiveSheet";
import { PauseModal } from "@/components/engine/PauseModal";
import { TriggerModal } from "@/components/engine/TriggerModal";
import { ConfigModal } from "@/components/engine/ConfigModal";
import { listJobs, resumeJob, triggerJob } from "@/api/engine";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions } from "@/hooks/usePermissions";
import { formatRelativeDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { JobConfig, JobRun } from "@/api/types";

type LoadStatus = "loading" | "ok" | "error";

/** Auto-refresh cadence while at least one job is mid-run. Same
 *  shape as JobRunsPage — keeps the table badges + recent-activity
 *  list current without a manual refresh. */
const JOBS_REFRESH_MS = 4000;
const IN_FLIGHT_RUN_STATUSES = new Set(["running", "pending", "started"]);

export default function EngineControlPage() {
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const canViewEngine = perms.canViewEngine;
  const navigate = useNavigate();
  const slug = project?.slug ?? null;

  const [jobs, setJobs] = useState<JobConfig[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);

  const [pauseTarget, setPauseTarget] = useState<JobConfig | null>(null);
  const [triggerTarget, setTriggerTarget] = useState<JobConfig | null>(null);
  const [configTarget, setConfigTarget] = useState<JobConfig | null>(null);
  // The freshly triggered job whose live execution sheet is open. The
  // sheet opens with `runId: null` *immediately* on click (before the
  // POST /trigger returns), so a fast job that finishes in 2 s still
  // gets a "Triggering…" → live-stream → success arc on screen instead
  // of a single late dump. `runId` is patched in once POST resolves.
  const [liveTarget, setLiveTarget] = useState<{
    job: JobConfig;
    runId: number | null;
  } | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const data = await listJobs(slug);
      setJobs(data);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractMessage(err));
      setStatus("error");
    }
  }, [slug]);

  /** Background refresh — same fetch without the loading flash and
   *  with transient errors swallowed, so the auto-poll while a job
   *  runs doesn't reset the table UI on every tick. */
  const refresh = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await listJobs(slug);
      setJobs(data);
    } catch {
      // keep the last good state on transient network errors
    }
  }, [slug]);

  useEffect(() => {
    if (canViewEngine) void load();
  }, [load, canViewEngine]);

  // Auto-refresh while any job's last run is mid-flight. The polling
  // cadence stops as soon as every job has settled, so a quiet engine
  // page doesn't beat on /jobs every 4 s for nothing.
  const hasInFlight = useMemo(
    () =>
      jobs.some(
        (j) =>
          !!j.last_run_status && IN_FLIGHT_RUN_STATUSES.has(j.last_run_status),
      ),
    [jobs],
  );
  useEffect(() => {
    if (!hasInFlight) return;
    const handle = setInterval(() => void refresh(), JOBS_REFRESH_MS);
    return () => clearInterval(handle);
  }, [hasInFlight, refresh]);

  useEffect(() => {
    if (slug && !canViewEngine) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, canViewEngine, navigate]);

  if (!canViewEngine) {
    return null;
  }

  function mergeJob(updated: JobConfig) {
    setJobs((prev) =>
      prev.map((j) => (j.job_id === updated.job_id ? updated : j)),
    );
  }

  async function handleResume(job: JobConfig) {
    if (!slug) return;
    setBusyJobId(job.job_id);
    try {
      const updated = await resumeJob(slug, job.job_id);
      mergeJob(updated);
      toast.success(`${job.name} resumed`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBusyJobId(null);
    }
  }

  async function handleTriggerConfirm(job: JobConfig) {
    if (!slug) return;
    // Close the confirmation modal and open the live sheet *before* we
    // fire the POST. The sheet shows a "Triggering…" placeholder until
    // the trigger response arrives carrying the run id.
    setTriggerTarget(null);
    setLiveTarget({ job, runId: null });
    try {
      const run = await triggerJob(slug, job.job_id);
      // Replace placeholder with the real run id — the sheet's effect
      // re-keys on runId and starts polling getRunDetail.
      setLiveTarget({ job, runId: run.id });
      toast.success("Job triggered");
      void load();
    } catch (err) {
      toast.error(extractMessage(err));
      setLiveTarget(null);
    }
  }

  function handleLiveComplete(run: JobRun) {
    if (!liveTarget) return;
    const label = liveTarget.job.name;
    if (run.status === "succeeded" || run.status === "success") {
      toast.success(`${label} completed`);
    } else if (run.status === "partial") {
      toast.warning(`${label} completed with errors`);
    } else if (run.status === "failed") {
      toast.error(`${label} failed`);
    } else {
      // cancelled / timed_out / skipped / unknown — surface verbatim.
      toast.message(`${label}: ${run.status}`);
    }
    void load();
  }

  return (
    <div className="space-y-6 pb-12">
      {slug && project?.config && "_cfo_workflow" in project.config && <CfoIncidentPanel key={slug} slug={slug} />}
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Engine Control
          </h1>
          <p className="text-sm text-slate-500">
            Automated jobs for {project?.name ?? "—"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={status === "loading"}
        >
          <RefreshCcw
            className={
              status === "loading"
                ? "mr-2 h-3.5 w-3.5 animate-spin"
                : "mr-2 h-3.5 w-3.5"
            }
          />
          Refresh
        </Button>
      </header>

      {status === "error" && (
        <ErrorBanner message={errorMessage} onRetry={load} />
      )}

      {status === "loading" ? (
        <JobsTableSkeleton rows={4} />
      ) : jobs.length === 0 ? (
        <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
          <p className="text-base font-medium text-slate-700">
            No jobs configured
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Job configurations are seeded by the project type.
          </p>
        </div>
      ) : (
        <JobsTable
          jobs={jobs}
          onPause={setPauseTarget}
          onResume={handleResume}
          onTrigger={setTriggerTarget}
          onConfigure={setConfigTarget}
          onViewRuns={(job) =>
            navigate(`/projects/${slug}/engine/${job.job_id}/runs`)
          }
          onRowClick={(job) =>
            navigate(`/projects/${slug}/engine/${job.job_id}/runs`)
          }
          canTrigger={perms.canTriggerEngine}
          canPauseResume={perms.canPauseResumeEngine}
          canConfigure={perms.canConfigureEngine}
          busyJobId={busyJobId}
        />
      )}

      <RecentActivity jobs={jobs} />

      {slug && (
        <>
          <PauseModal
            job={pauseTarget}
            projectSlug={slug}
            open={pauseTarget !== null}
            onOpenChange={(o) => !o && setPauseTarget(null)}
            onSuccess={mergeJob}
          />
          <TriggerModal
            job={triggerTarget}
            open={triggerTarget !== null}
            onOpenChange={(o) => !o && setTriggerTarget(null)}
            onConfirm={handleTriggerConfirm}
          />
          <ConfigModal
            job={configTarget}
            projectSlug={slug}
            open={configTarget !== null}
            onOpenChange={(o) => !o && setConfigTarget(null)}
            onSuccess={mergeJob}
          />
          {liveTarget && (
            <JobRunLiveSheet
              open
              onClose={() => setLiveTarget(null)}
              projectSlug={slug}
              jobId={liveTarget.job.job_id}
              jobName={liveTarget.job.name}
              runId={liveTarget.runId}
              mode="live"
              onComplete={handleLiveComplete}
              onRetry={() => setTriggerTarget(liveTarget.job)}
            />
          )}
        </>
      )}
    </div>
  );
}

function RecentActivity({ jobs }: { jobs: JobConfig[] }) {
  const items = jobs
    .filter((j) => j.last_run_at != null)
    .slice()
    .sort((a, b) =>
      (b.last_run_at ?? "").localeCompare(a.last_run_at ?? ""),
    )
    .slice(0, 6);

  if (items.length === 0) return null;

  return (
    <section>
      <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        Recent activity
      </h2>
      <ol className="space-y-2 rounded-plane bg-white p-4 shadow-plane">
        {items.map((job) => (
          <li key={job.job_id} className="flex items-center gap-3 text-sm">
            <RunStatusGlyph status={job.last_run_status} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">
                <span className="font-medium text-slate-900">{job.name}</span>{" "}
                <span className="text-slate-500">
                  {job.last_run_status === "succeeded" || job.last_run_status === "success"
                    ? "completed successfully"
                    : job.last_run_status === "partial"
                      ? "completed with errors"
                      : job.last_run_status === "failed"
                      ? "failed during execution"
                      : job.last_run_status === "running"
                        ? "is running"
                        : (job.last_run_status ?? "ran")}
                </span>
              </div>
            </div>
            <span className="text-xs text-slate-400">
              {formatRelativeDate(job.last_run_at)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function RunStatusGlyph({ status }: { status: string | null }) {
  const key = (status ?? "").toLowerCase();
  if (key === "running") {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
      </span>
    );
  }
  if (key === "succeeded" || key === "success") {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      </span>
    );
  }
  if (key === "failed" || key === "partial") {
    return (
      <span
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded-full",
          key === "partial" ? "bg-orange-50" : "bg-rose-50",
        )}
      >
        <XCircle className={cn("h-3 w-3", key === "partial" ? "text-orange-500" : "text-rose-500")} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "flex h-5 w-5 items-center justify-center rounded-full bg-slate-100",
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
    </span>
  );
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-rose-700">
          Couldn&rsquo;t load jobs
        </p>
        {message && <p className="text-xs text-rose-600">{message}</p>}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
      >
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
